import { useEffect, useRef, useState, type PointerEvent as RPointerEvent, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { cn } from "../lib/cn";
import { useUI } from "../lib/uiStore";

export type DeckPane = "communities" | "feed";

/**
 * Communities and Feed live side by side on a horizontal track and stay
 * mounted, so scroll position, drafts and loaded data survive switching.
 * Touch devices can swipe between them; desktop uses the rail and [ / ].
 */
export function Deck({ active, communities, feed }: { active: DeckPane | null; communities: ReactNode; feed: ReactNode }) {
  const navigate = useNavigate();
  const lastCommunityPath = useUI((s) => s.lastCommunityPath);
  // The pane on screen: the active one, or the last one while another page covers the deck.
  const lastShown = useRef<DeckPane>(active ?? "feed");
  if (active) lastShown.current = active;
  const shown = active ?? lastShown.current;
  // Only slide between panes while the deck is already on screen. Arriving from another
  // page (Explore, Messages…) must jump straight to the pane rather than sliding across.
  const wasVisible = useRef(!!active);
  const animate = wasVisible.current && !!active;
  useEffect(() => {
    wasVisible.current = !!active;
  });
  const [mounted, setMounted] = useState({ communities: active === "communities", feed: active === "feed" || active === null });
  const trackRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; y: number; t: number; locked: "h" | "v" | null; dx: number; id: number } | null>(null);
  const [dragX, setDragX] = useState<number | null>(null);

  useEffect(() => {
    if (!active) return;
    setMounted((m) => (m[active] ? m : { ...m, [active]: true }));
  }, [active]);

  const go = (pane: DeckPane) => navigate(pane === "feed" ? "/feed" : lastCommunityPath || "/c");

  const onPointerDown = (e: RPointerEvent) => {
    if (e.pointerType !== "touch" || !active) return;
    if ((e.target as HTMLElement).closest("[data-swipe-ignore],input,textarea,[contenteditable=true]")) return;
    drag.current = { x: e.clientX, y: e.clientY, t: performance.now(), locked: null, dx: 0, id: e.pointerId };
  };
  const onPointerMove = (e: RPointerEvent) => {
    const d = drag.current;
    if (!d || e.pointerId !== d.id) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (!d.locked) {
      if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
      d.locked = Math.abs(dx) > Math.abs(dy) * 1.3 ? "h" : "v";
      if (d.locked === "v") {
        drag.current = null;
        return;
      }
    }
    // Resist dragging past the edges.
    const atEdge = (shown === "communities" && dx > 0) || (shown === "feed" && dx < 0);
    d.dx = atEdge ? dx * 0.2 : dx;
    setDragX(d.dx);
  };
  const onPointerEnd = () => {
    const d = drag.current;
    drag.current = null;
    if (!d || d.locked !== "h") return setDragX(null);
    const width = trackRef.current?.parentElement?.clientWidth ?? window.innerWidth;
    const velocity = d.dx / Math.max(1, performance.now() - d.t);
    setDragX(null);
    if (shown === "communities" && (d.dx < -width * 0.25 || velocity < -0.5)) go("feed");
    else if (shown === "feed" && (d.dx > width * 0.25 || velocity > 0.5)) go("communities");
  };

  const base = shown === "feed" ? -50 : 0;
  return (
    <div
      className={cn("absolute inset-0 overflow-hidden", !active && "invisible")}
      aria-hidden={!active}
      inert={!active || undefined}
    >
      <div
        ref={trackRef}
        className={cn("flex h-full w-[200%] touch-pan-y", dragX === null && animate && "transition-transform duration-300 ease-[cubic-bezier(.2,.8,.2,1)]")}
        style={{ transform: `translateX(calc(${base}% + ${dragX ?? 0}px))` }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
      >
        <section aria-label="Communities" className="relative h-full w-1/2 min-w-0" inert={shown !== "communities" || undefined}>
          {mounted.communities && communities}
        </section>
        <section aria-label="Feed" className="relative h-full w-1/2 min-w-0" inert={shown !== "feed" || undefined}>
          {mounted.feed && feed}
        </section>
      </div>
    </div>
  );
}
